import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Phone, Mail, PhoneCall, BarChart3, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ContactLeadActionsMenu } from "@/components/admin/ContactLeadActionsMenu";
import { CallActionsMenu } from "@/components/admin/CallActionsMenu";
import { CopyButton } from "@/components/admin/CopyButton";
import { SEOHead } from "@/components/SEOHead";

interface UnifiedLead {
  id: string;
  source: "contact" | "phone";
  name: string;
  phone: string;
  email: string | null;
  service: string | null;
  lead_status: string;
  priority: string;
  lead_quality: string | null;
  estimated_value: number | null;
  created_at: string;
  call_duration?: number | null;
  recording_url?: string | null;
  message?: string | null;
  rawData: any;
}

const AllLeads = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [leads, setLeads] = useState<UnifiedLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [leadStatusFilter, setLeadStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    contact: 0,
    phone: 0,
    new: 0,
    converted: 0,
  });
  const [statsOpen, setStatsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadAllLeads();
    }
  }, [user]);

  const loadAllLeads = async () => {
    setIsLoading(true);
    
    // Load contact form leads
    const { data: contactData, error: contactError } = await supabase
      .from("contact_submissions")
      .select("*")
      .order("created_at", { ascending: false });

    // Load phone leads
    const { data: phoneData, error: phoneError } = await supabase
      .from("phone_leads")
      .select("*")
      .order("created_at", { ascending: false });

    if (contactError || phoneError) {
      toast({
        title: "Error",
        description: "Failed to load leads",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    // Combine and normalize the data
    const unifiedLeads: UnifiedLead[] = [];

    if (contactData) {
      contactData.forEach((contact) => {
        unifiedLeads.push({
          id: contact.id,
          source: "contact",
          name: contact.name,
          phone: contact.phone,
          email: contact.email,
          service: contact.service,
          lead_status: contact.lead_status || contact.status,
          priority: contact.priority || "medium",
          lead_quality: contact.lead_quality,
          estimated_value: contact.estimated_value,
          created_at: contact.created_at,
          message: contact.message,
          rawData: contact,
        });
      });
    }

    if (phoneData) {
      phoneData.forEach((call) => {
        unifiedLeads.push({
          id: call.id,
          source: "phone",
          name: call.customer_name || call.caller_phone || "Unknown",
          phone: call.caller_phone || call.phone_number,
          email: call.customer_email,
          service: call.service_interest,
          lead_status: call.lead_status || call.status,
          priority: call.priority || "medium",
          lead_quality: call.lead_quality,
          estimated_value: call.estimated_value,
          created_at: call.created_at,
          call_duration: call.call_duration,
          recording_url: call.recording_url,
          rawData: call,
        });
      });
    }

    // Sort by date
    unifiedLeads.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    setLeads(unifiedLeads);
    setStats({
      total: unifiedLeads.length,
      contact: unifiedLeads.filter((l) => l.source === "contact").length,
      phone: unifiedLeads.filter((l) => l.source === "phone").length,
      new: unifiedLeads.filter((l) => l.lead_status === "new").length,
      converted: unifiedLeads.filter((l) => l.lead_status === "converted").length,
    });
    setIsLoading(false);
  };

  const updateContactStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ lead_status: status, last_contact_date: new Date().toISOString() })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } else {
      toast({ title: "Success", description: "Status updated successfully" });
      loadAllLeads();
    }
  };

  const updateContactPriority = async (id: string, priority: string) => {
    const { error } = await supabase
      .from("contact_submissions")
      .update({ priority })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update priority",
        variant: "destructive",
      });
    } else {
      toast({ title: "Success", description: "Priority updated successfully" });
      loadAllLeads();
    }
  };

  const updatePhoneStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("phone_leads")
      .update({ lead_status: status })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    } else {
      toast({ title: "Success", description: "Status updated successfully" });
      loadAllLeads();
    }
  };

  const updatePhonePriority = async (id: string, priority: string) => {
    const { error } = await supabase
      .from("phone_leads")
      .update({ priority })
      .eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update priority",
        variant: "destructive",
      });
    } else {
      toast({ title: "Success", description: "Priority updated successfully" });
      loadAllLeads();
    }
  };

  const exportToCSV = () => {
    const headers = ["Source", "Name", "Phone", "Email", "Service", "Status", "Priority", "Quality", "Created At"];
    const csvData = filteredLeads.map((lead) => [
      lead.source,
      lead.name,
      lead.phone,
      lead.email || "N/A",
      lead.service || "N/A",
      lead.lead_status,
      lead.priority,
      lead.lead_quality || "N/A",
      new Date(lead.created_at).toLocaleString(),
    ]);

    const csv = [headers, ...csvData].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `all-leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      new: "default",
      contacted: "secondary",
      qualified: "outline",
      converted: "default",
      unqualified: "destructive",
      callback_scheduled: "secondary",
      lost: "destructive",
    };
    return variants[status] || "default";
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      urgent: "destructive",
      high: "default",
      medium: "secondary",
      low: "outline",
    };
    return variants[priority] || "secondary";
  };

  const activeFiltersCount = [
    searchTerm,
    sourceFilter !== "all" && sourceFilter,
    leadStatusFilter !== "all" && leadStatusFilter,
    priorityFilter !== "all" && priorityFilter,
  ].filter(Boolean).length;

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSource = sourceFilter === "all" || lead.source === sourceFilter;
    const matchesLeadStatus = leadStatusFilter === "all" || lead.lead_status === leadStatusFilter;
    const matchesPriority = priorityFilter === "all" || lead.priority === priorityFilter;

    return matchesSearch && matchesSource && matchesLeadStatus && matchesPriority;
  });

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading leads...</div>;
  }

  return (
    <>
      <SEOHead title="All Leads" description="Unified view of all leads" />
      
      <div className="space-y-4 md:space-y-6">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setStatsOpen(!statsOpen)}
            className="flex-1 h-9"
          >
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Stats: {stats.total}</span>
          </Button>

          <Button
            variant="outline"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex-1 h-9"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">
              Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </span>
          </Button>

          <Button
            variant="outline"
            onClick={exportToCSV}
            className="flex-1 h-9"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline ml-2">Export</span>
          </Button>
        </div>

        {statsOpen && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-xs md:text-sm font-medium">Total Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl md:text-2xl font-bold">{stats.total}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-xs md:text-sm font-medium">Contact Form</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl md:text-2xl font-bold">{stats.contact}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-xs md:text-sm font-medium">Phone Calls</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl md:text-2xl font-bold">{stats.phone}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-xs md:text-sm font-medium">New</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl md:text-2xl font-bold">{stats.new}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2 md:pb-3">
                <CardTitle className="text-xs md:text-sm font-medium">Converted</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xl md:text-2xl font-bold">{stats.converted}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {filtersOpen && (
          <Card>
            <CardHeader className="px-4 py-3 md:p-6">
              <CardTitle className="text-base md:text-lg">Filters</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 md:p-6 md:pt-0">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="search" className="text-sm">Search</Label>
                  <Input
                    id="search"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-11 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source" className="text-sm">Source</Label>
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger id="source" className="h-11">
                      <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="contact">Contact Form</SelectItem>
                      <SelectItem value="phone">Phone Call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead-status" className="text-sm">Lead Status</Label>
                  <Select value={leadStatusFilter} onValueChange={setLeadStatusFilter}>
                    <SelectTrigger id="lead-status" className="h-11">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority" className="text-sm">Priority</Label>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger id="priority" className="h-11">
                      <SelectValue placeholder="All priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {filteredLeads.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">No leads found</p>
              </CardContent>
            </Card>
          ) : (
            filteredLeads.map((lead) => (
              <Card key={`${lead.source}-${lead.id}`}>
                <CardContent className="p-4 md:pt-6">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-2 md:space-y-3 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {lead.source === "contact" ? <Mail className="h-3 w-3 mr-1" /> : <PhoneCall className="h-3 w-3 mr-1" />}
                          {lead.source === "contact" ? "Contact Form" : "Phone Call"}
                        </Badge>
                        <h3 className="text-base md:text-lg font-semibold truncate flex-shrink min-w-0">{lead.name}</h3>
                        <Badge variant={getStatusBadge(lead.lead_status)} className="text-xs">
                          {lead.lead_status.replace("_", " ")}
                        </Badge>
                        <Badge variant={getPriorityBadge(lead.priority)} className="text-xs">{lead.priority}</Badge>
                        {lead.lead_quality && (
                          <Badge variant="outline" className="capitalize text-xs">
                            {lead.lead_quality}
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 text-sm">
                        <div className="flex items-center gap-2 min-h-[44px] md:min-h-0">
                          <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <a href={`tel:${lead.phone}`} className="hover:underline truncate">
                            {lead.phone}
                          </a>
                          <CopyButton text={lead.phone} label="Phone" />
                        </div>
                        {lead.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <a href={`mailto:${lead.email}`} className="hover:underline">
                              {lead.email}
                            </a>
                            <CopyButton text={lead.email} label="Email" />
                          </div>
                        )}
                        {lead.service && (
                          <div>
                            <span className="font-medium">Service:</span> {lead.service}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Created:</span>{" "}
                          {new Date(lead.created_at).toLocaleString()}
                        </div>
                        {lead.call_duration && (
                          <div>
                            <span className="font-medium">Duration:</span> {lead.call_duration}s
                          </div>
                        )}
                        {lead.estimated_value && (
                          <div>
                            <span className="font-medium">Est. Value:</span> ${lead.estimated_value}
                          </div>
                        )}
                      </div>

                      {lead.message && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground font-medium mb-1">Message:</p>
                          <p className="text-sm">{lead.message}</p>
                        </div>
                      )}
                    </div>

                    {lead.source === "contact" ? (
                      <ContactLeadActionsMenu
                        contactId={lead.id}
                        contactData={lead.rawData}
                        onStatusUpdate={updateContactStatus}
                        onPriorityUpdate={updateContactPriority}
                        onRefresh={loadAllLeads}
                      />
                    ) : (
                      <CallActionsMenu
                        callId={lead.id}
                        callData={lead.rawData}
                        onStatusUpdate={updatePhoneStatus}
                        onPriorityUpdate={updatePhonePriority}
                        onRefresh={loadAllLeads}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default AllLeads;
